import axios from 'axios';
import type {
  Task, TaskCreate, DashboardStats,
  Requirement, Menu, Report, LoginResponse,
  RfpDocument, RfpAnalysisResult,
  ScreenDesign, ScreenDesignCreate, ScreenDesignUpdate,
  ScreenDesignVersion, ScreenDesignVersionCreate,
  TableDefinition, TableDefinitionCreate, TableDefinitionUpdate,
  TableColumn, TableColumnCreate,
  TableDefinitionDetail,
  TableIndex, TableIndexCreate,
  TableConstraint, TableConstraintCreate,
  TableForeignKey, TableForeignKeyCreate,
  ApiSpec, ApiSpecCreate, ApiSpecUpdate,
  ArchitectureComponent, ArchitectureComponentCreate, ArchitectureComponentUpdate,
  ArchitectureConnection, ArchitectureConnectionCreate, ArchitectureConnectionUpdate,
  ArchitectureFullResponse,
  DevTask, DevTaskCreate, DevTaskUpdate, WbsDates, DevTaskAutoGenerateResponse
} from '@/types';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5010/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 180000, // 180초 타임아웃 (RFP 분석 등 긴 작업 대응)
  maxContentLength: 50 * 1024 * 1024, // 50MB
  maxBodyLength: 50 * 1024 * 1024, // 50MB
});

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('wbs_auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Request interceptor - add project context
const GLOBAL_PATHS = ['/auth', '/projects', '/settings', '/categories', '/category-items', '/common-codes', '/storage', '/accessibility', '/department-permissions', '/stage-artifact-mappings', '/notifications'];

api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const isGlobal = GLOBAL_PATHS.some(p => config.url?.startsWith(p));
    if (!isGlobal) {
      const stored = localStorage.getItem('omnimind_project');
      if (stored) {
        try {
          const { state } = JSON.parse(stored);
          if (state?.currentProjectId) {
            config.headers['X-Project-Id'] = String(state.currentProjectId);
          }
        } catch { /* ignore */ }
      }
    }
  }
  return config;
});

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('wbs_auth_token');
        localStorage.removeItem('wbs_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: async (username: string, password: string): Promise<LoginResponse> => {
    const { data } = await api.post('/auth/login', { username, password });
    return data;
  },
  logout: async () => {
    const { data } = await api.post('/auth/logout');
    return data;
  },
  verify: async (token: string) => {
    const { data } = await api.get(`/auth/verify?token=${token}`);
    return data;
  },
};

// Tasks
export const tasksApi = {
  getAll: async (filters?: { status?: string; manager?: string }): Promise<Task[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.manager) params.append('manager', filters.manager);
    const { data } = await api.get(`/tasks?${params.toString()}`);
    return data;
  },
  getById: async (id: number): Promise<Task> => {
    const { data } = await api.get(`/tasks/${id}`);
    return data;
  },
  exportToSheets: async (): Promise<{ success: boolean; spreadsheet_id: string; spreadsheet_url: string; message: string }> => {
    const { data } = await api.post('/tasks/export-to-sheets');
    return data;
  },
  create: async (task: TaskCreate): Promise<Task> => {
    const { data } = await api.post('/tasks', task);
    return data;
  },
  update: async (id: number, task: Partial<TaskCreate>): Promise<Task> => {
    const { data } = await api.put(`/tasks/${id}`, task);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  },
  bulkDelete: async (ids: number[]): Promise<void> => {
    await api.delete('/tasks', { data: { ids } });
  },
  reorder: async (items: Array<{ id: number; order_index: number; parent_id?: number | null; stage_name?: string; depth?: number }>): Promise<void> => {
    await api.put('/tasks/reorder', items);
  },
  getStageArtifacts: async (stageName: string): Promise<StageArtifacts> => {
    const { data } = await api.get(`/wbs/stage-artifacts/${encodeURIComponent(stageName)}`);
    return data;
  },
};

// Stage Artifacts
export interface StageArtifact {
  key: string;
  label: string;
  link: string;
  icon: string;
  total: number;
  done: number;
  wip: number;
  draft: number;
  progress: number;
  has_alert: boolean;
}

export interface StageArtifacts {
  stage_name: string;
  artifacts: StageArtifact[];
  total_progress: number;
}

// Dashboard
export interface StageProgress {
  stage_name: string;
  task_count: number;
  completed_count: number;
  progress: number;
  color: string;
}

export interface ProjectSummary {
  id: number;
  name: string;
  code: string;
  client?: string;
  start_date: string;
  end_date: string;
  status: string;
  d_day: number;
  elapsed_days: number;
  total_days: number;
  elapsed_percent: number;
}

export interface DeliverablesSummary {
  requirements: { total: number; accepted: number; pending: number; rejected: number };
  menus: { total: number; depth1: number; depth2: number; depth3: number };
  screens: { total: number; completed: number; in_progress: number; draft: number };
  tables: { total: number; completed: number; in_progress: number; draft: number };
  apis: { total: number; completed: number; in_progress: number; draft: number };
  test_scenarios: { total: number; completed: number; in_progress: number; draft: number };
  test_cases: { total: number; completed: number; in_progress: number; draft: number };
}

export interface TeamSummary {
  total_members: number;
  by_department: Record<string, number>;
  current_month_mm: number;
  total_mm: number;
}

export interface DashboardIssueSummary {
  total: number;
  new_count: number;
  in_progress: number;
  resolved: number;
  overdue: number;
  recent_issues: {
    id: number;
    issue_id: string;
    title: string;
    status: string;
    priority: string;
    assignee?: string;
    due_date?: string;
  }[];
}

export interface TestCoverage {
  total_requirements: number;
  covered_requirements: number;
  coverage_percent: number;
  uncovered_requirements: { id: number; req_id: string; title: string; category: string }[];
  test_pass_rate: number;
  test_summary: { total: number; pass: number; fail: number; pending: number };
}

export interface DashboardExtended {
  project: ProjectSummary;
  overall_progress: number;
  week_progress: number;
  stage_progress: StageProgress[];
  status_counts: { before: number; progress: number; confirm: number; complete: number };
  deliverables: DeliverablesSummary;
  test_coverage?: TestCoverage;
  team?: TeamSummary;
  issues?: DashboardIssueSummary;
  recent_activities?: { type: string; message: string; time: string }[];
}

// Issue
export interface Issue {
  id: number;
  issue_id: string;
  project_id: number;
  title: string;
  description?: string;
  category: string;
  priority: string;
  severity: string;
  status: string;
  reporter?: string;
  assignee?: string;
  reported_date?: string;
  due_date?: string;
  resolved_date?: string;
  resolution?: string;
  created_at: string;
  updated_at: string;
}

export interface IssueCreate {
  project_id: number;
  title: string;
  description?: string;
  category?: string;
  priority?: string;
  severity?: string;
  status?: string;
  reporter?: string;
  assignee?: string;
  reported_date?: string;
  due_date?: string;
}

export interface IssueUpdate {
  title?: string;
  description?: string;
  category?: string;
  priority?: string;
  severity?: string;
  status?: string;
  reporter?: string;
  assignee?: string;
  reported_date?: string;
  due_date?: string;
  resolved_date?: string;
  resolution?: string;
}

export const dashboardApi = {
  getStats: async (): Promise<DashboardStats> => {
    const { data } = await api.get('/dashboard');
    return data;
  },
  getManagers: async (): Promise<string[]> => {
    const { data } = await api.get('/managers');
    return data;
  },
  getExtended: async (projectId?: number): Promise<DashboardExtended> => {
    const params = projectId ? { project_id: projectId } : {};
    const { data } = await api.get('/dashboard/extended', { params });
    return data;
  },
};

// Issues
export const issuesApi = {
  getAll: async (filters?: { project_id?: number; status?: string; priority?: string; category?: string }): Promise<Issue[]> => {
    const params = new URLSearchParams();
    if (filters?.project_id) params.append('project_id', String(filters.project_id));
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.category) params.append('category', filters.category);
    const { data } = await api.get(`/issues?${params.toString()}`);
    return data;
  },
  getOne: async (id: number): Promise<Issue> => {
    const { data } = await api.get(`/issues/${id}`);
    return data;
  },
  create: async (issue: IssueCreate): Promise<Issue> => {
    const { data } = await api.post('/issues', issue);
    return data;
  },
  update: async (id: number, issue: IssueUpdate): Promise<Issue> => {
    const { data } = await api.put(`/issues/${id}`, issue);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/issues/${id}`);
  },
  getCategories: async (): Promise<string[]> => {
    const { data } = await api.get('/issues/options/categories');
    return data;
  },
  getPriorities: async (): Promise<string[]> => {
    const { data } = await api.get('/issues/options/priorities');
    return data;
  },
  getStatuses: async (): Promise<string[]> => {
    const { data } = await api.get('/issues/options/statuses');
    return data;
  },
};

// Requirements
export const requirementsApi = {
  getAll: async (filters?: { acceptance?: string; category?: string }): Promise<Requirement[]> => {
    const params = new URLSearchParams();
    if (filters?.acceptance) params.append('acceptance', filters.acceptance);
    if (filters?.category) params.append('category', filters.category);
    const { data } = await api.get(`/requirements?${params.toString()}`);
    return data;
  },
  create: async (req: Partial<Requirement>): Promise<Requirement> => {
    const { data } = await api.post('/requirements', req);
    return data;
  },
  update: async (id: number, req: Partial<Requirement>): Promise<Requirement> => {
    const { data } = await api.put(`/requirements/${id}`, req);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/requirements/${id}`);
  },
  bulkDelete: async (ids: number[]): Promise<void> => {
    await api.delete('/requirements', { data: { ids } });
  },
};

// Menus
export const menusApi = {
  getAll: async (): Promise<Menu[]> => {
    const { data } = await api.get('/menus');
    return data;
  },
  getTree: async (): Promise<Menu[]> => {
    const { data } = await api.get('/menus/tree');
    return data;
  },
  create: async (menu: Partial<Menu>): Promise<Menu> => {
    const { data } = await api.post('/menus', menu);
    return data;
  },
  update: async (id: number, menu: Partial<Menu>): Promise<Menu> => {
    const { data } = await api.put(`/menus/${id}`, menu);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/menus/${id}`);
  },
  exportToSheets: async (): Promise<{ success: boolean; spreadsheet_id: string; spreadsheet_url: string; message: string }> => {
    const { data } = await api.post('/menus/export-to-sheets');
    return data;
  },
  exportFsdToSheets: async (): Promise<{ success: boolean; spreadsheet_id: string; spreadsheet_url: string; message: string }> => {
    const { data } = await api.post('/menus/export-fsd-to-sheets');
    return data;
  },
};

// Reports
export const reportsApi = {
  getAll: async (filters?: { report_type?: string; year_month?: string }): Promise<Report[]> => {
    const params = new URLSearchParams();
    if (filters?.report_type) params.append('report_type', filters.report_type);
    if (filters?.year_month) params.append('year_month', filters.year_month);
    const { data } = await api.get(`/reports?${params.toString()}`);
    return data;
  },
  getById: async (id: number): Promise<Report> => {
    const { data } = await api.get(`/reports/${id}`);
    return data;
  },
  create: async (report: Partial<Report>): Promise<Report> => {
    const { data } = await api.post('/reports', report);
    return data;
  },
  update: async (id: number, report: Partial<Report>): Promise<Report> => {
    const { data } = await api.put(`/reports/${id}`, report);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/reports/${id}`);
  },
  calculateProgress: async () => {
    const { data } = await api.get('/reports/progress/calculate');
    return data;
  },
};

// AI 코드 분석 결과 타입
export interface CodeAnalysisResult {
  file_path: string;
  summary: string;
  functions: {
    name: string;
    description: string;
    params: string[];
    returns: string;
  }[];
  dependencies: string[];
  complexity: 'low' | 'medium' | 'high';
  suggestions: string[];
}

// AI
export const aiApi = {
  proofread: async (content: string, type: string = 'achievements'): Promise<string[]> => {
    const { data } = await api.post('/ai/proofread', { content, type });
    return data.result;
  },
  summary: async (reportData: object): Promise<string> => {
    const { data } = await api.post('/ai/summary', reportData);
    return data.result;
  },
  generate: async (prompt: string): Promise<{ success: boolean; result: string }> => {
    const { data } = await api.post('/ai/generate', { prompt });
    return data;
  },
  analyzeCode: async (content: string, path: string, language?: string): Promise<CodeAnalysisResult> => {
    const { data } = await api.post('/ai/analyze-code', { content, path, language });
    return data.result;
  },
};

// RFP
export const rfpApi = {
  getAll: async (): Promise<RfpDocument[]> => {
    const { data } = await api.get('/rfp');
    return data;
  },
  getById: async (id: number): Promise<RfpDocument> => {
    const { data } = await api.get(`/rfp/${id}`);
    return data;
  },
  upload: async (file: File): Promise<RfpDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/rfp/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  analyze: async (rfpId: number): Promise<RfpAnalysisResult> => {
    const { data } = await api.post(`/rfp/${rfpId}/analyze`);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/rfp/${id}`);
  },
};

// Screen Designs (화면설계서)
export const screenDesignsApi = {
  getAll: async (): Promise<ScreenDesign[]> => {
    const { data } = await api.get('/screen-designs');
    return data;
  },
  getById: async (id: number): Promise<ScreenDesign> => {
    const { data } = await api.get(`/screen-designs/${id}`);
    return data;
  },
  create: async (screen: ScreenDesignCreate): Promise<ScreenDesign> => {
    const { data } = await api.post('/screen-designs', screen);
    return data;
  },
  update: async (id: number, screen: ScreenDesignUpdate): Promise<ScreenDesign> => {
    const { data } = await api.put(`/screen-designs/${id}`, screen);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/screen-designs/${id}`);
  },
  // 버전 관리
  getVersions: async (screenId: number): Promise<ScreenDesignVersion[]> => {
    const { data } = await api.get(`/screen-designs/${screenId}/versions`);
    return data;
  },
  createVersion: async (screenId: number, version: ScreenDesignVersionCreate): Promise<ScreenDesignVersion> => {
    const { data } = await api.post(`/screen-designs/${screenId}/versions`, version);
    return data;
  },
  deleteVersion: async (screenId: number, versionId: number): Promise<void> => {
    await api.delete(`/screen-designs/${screenId}/versions/${versionId}`);
  },
  downloadVersion: async (screenId: number, versionId: number) => {
    const { data } = await api.get(`/screen-designs/${screenId}/versions/${versionId}/download`);
    return data;
  },
  previewGdriveFile: async (screenId: number, versionId: number): Promise<{ html_content: string }> => {
    const { data } = await api.get(`/screen-designs/${screenId}/versions/${versionId}/preview`);
    return data;
  },
  downloadVersionPdf: async (screenId: number, versionId: number): Promise<Blob> => {
    const { data } = await api.get(`/screen-designs/${screenId}/versions/${versionId}/pdf`, {
      responseType: 'blob',
    });
    return data;
  },
  // 코멘트
  getComments: async (screenId: number): Promise<ScreenDesignComment[]> => {
    const { data } = await api.get(`/screen-designs/${screenId}/comments`);
    return data;
  },
  createComment: async (screenId: number, content: string): Promise<ScreenDesignComment> => {
    const { data } = await api.post(`/screen-designs/${screenId}/comments`, { content });
    return data;
  },
  updateComment: async (screenId: number, commentId: number, content: string): Promise<ScreenDesignComment> => {
    const { data } = await api.put(`/screen-designs/${screenId}/comments/${commentId}`, { content });
    return data;
  },
  deleteComment: async (screenId: number, commentId: number): Promise<void> => {
    await api.delete(`/screen-designs/${screenId}/comments/${commentId}`);
  },
};

export interface ScreenDesignComment {
  id: number;
  screen_design_id: number;
  user_id: number;
  content: string;
  username: string | null;
  display_name: string | null;
  created_at: string;
  updated_at: string;
}

// Table Definitions (테이블정의서)
export const tableDefinitionsApi = {
  getAll: async (filters?: { search?: string; status?: string }): Promise<TableDefinition[]> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    const { data } = await api.get(`/table-definitions?${params.toString()}`);
    return data;
  },
  getById: async (id: number): Promise<TableDefinition> => {
    const { data } = await api.get(`/table-definitions/${id}`);
    return data;
  },
  getDetail: async (id: number): Promise<TableDefinitionDetail> => {
    const { data } = await api.get(`/table-definitions/${id}/detail`);
    return data;
  },
  create: async (tableData: TableDefinitionCreate): Promise<TableDefinition> => {
    const { data } = await api.post('/table-definitions', tableData);
    return data;
  },
  update: async (id: number, tableData: TableDefinitionUpdate): Promise<TableDefinition> => {
    const { data } = await api.put(`/table-definitions/${id}`, tableData);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/table-definitions/${id}`);
  },
  // 컬럼 관리
  createColumn: async (tableId: number, columnData: TableColumnCreate): Promise<TableColumn> => {
    const { data } = await api.post(`/table-definitions/${tableId}/columns`, columnData);
    return data;
  },
  updateColumn: async (tableId: number, columnId: number, columnData: Partial<TableColumnCreate>): Promise<TableColumn> => {
    const { data } = await api.put(`/table-definitions/${tableId}/columns/${columnId}`, columnData);
    return data;
  },
  deleteColumn: async (tableId: number, columnId: number): Promise<void> => {
    await api.delete(`/table-definitions/${tableId}/columns/${columnId}`);
  },
  // 인덱스 관리
  createIndex: async (tableId: number, indexData: TableIndexCreate): Promise<TableIndex> => {
    const { data } = await api.post(`/table-definitions/${tableId}/indexes`, indexData);
    return data;
  },
  updateIndex: async (tableId: number, indexId: number, indexData: Partial<TableIndexCreate>): Promise<TableIndex> => {
    const { data } = await api.put(`/table-definitions/${tableId}/indexes/${indexId}`, indexData);
    return data;
  },
  deleteIndex: async (tableId: number, indexId: number): Promise<void> => {
    await api.delete(`/table-definitions/${tableId}/indexes/${indexId}`);
  },
  // 제약조건 관리
  createConstraint: async (tableId: number, constraintData: TableConstraintCreate): Promise<TableConstraint> => {
    const { data } = await api.post(`/table-definitions/${tableId}/constraints`, constraintData);
    return data;
  },
  updateConstraint: async (tableId: number, constraintId: number, constraintData: Partial<TableConstraintCreate>): Promise<TableConstraint> => {
    const { data } = await api.put(`/table-definitions/${tableId}/constraints/${constraintId}`, constraintData);
    return data;
  },
  deleteConstraint: async (tableId: number, constraintId: number): Promise<void> => {
    await api.delete(`/table-definitions/${tableId}/constraints/${constraintId}`);
  },
  // 외래키 관리
  createForeignKey: async (tableId: number, fkData: TableForeignKeyCreate): Promise<TableForeignKey> => {
    const { data } = await api.post(`/table-definitions/${tableId}/foreign-keys`, fkData);
    return data;
  },
  updateForeignKey: async (tableId: number, fkId: number, fkData: Partial<TableForeignKeyCreate>): Promise<TableForeignKey> => {
    const { data } = await api.put(`/table-definitions/${tableId}/foreign-keys/${fkId}`, fkData);
    return data;
  },
  deleteForeignKey: async (tableId: number, fkId: number): Promise<void> => {
    await api.delete(`/table-definitions/${tableId}/foreign-keys/${fkId}`);
  },
  // 엑셀 기능
  downloadExcelTemplate: async (): Promise<{ filename: string; data: string }> => {
    const { data } = await api.get('/table-definitions/excel/template');
    return data;
  },
  importColumnsFromExcel: async (tableId: number, fileData: string): Promise<{ success: boolean; message: string; columns_count: number }> => {
    const { data } = await api.post(`/table-definitions/${tableId}/columns/import-excel`, { file_data: fileData });
    return data;
  },
};

// API Specs (API 명세서)
export const apiSpecsApi = {
  getAll: async (category?: string, method?: string, status?: string, full?: boolean): Promise<ApiSpec[]> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (method) params.append('method', method);
    if (status) params.append('status', status);
    if (full) params.append('full', 'true');
    const { data } = await api.get(`/api-specs?${params.toString()}`);
    return data;
  },
  getById: async (id: number): Promise<ApiSpec> => {
    const { data } = await api.get(`/api-specs/${id}`);
    return data;
  },
  create: async (apiData: ApiSpecCreate): Promise<ApiSpec> => {
    const { data } = await api.post('/api-specs', apiData);
    return data;
  },
  update: async (id: number, apiData: ApiSpecUpdate): Promise<ApiSpec> => {
    const { data } = await api.put(`/api-specs/${id}`, apiData);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/api-specs/${id}`);
  },
  getCategories: async (): Promise<string[]> => {
    const { data } = await api.get('/api-specs/categories/list');
    return data;
  },
};

// Architecture (시스템 아키텍처)
export const architectureApi = {
  // 전체 조회 (컴포넌트 + 연결)
  getAll: async (): Promise<ArchitectureFullResponse> => {
    const { data } = await api.get('/architecture');
    return data;
  },
  // 컴포넌트
  getComponents: async (filters?: { component_type?: string; layer?: string; status?: string }): Promise<ArchitectureComponent[]> => {
    const params = new URLSearchParams();
    if (filters?.component_type) params.append('component_type', filters.component_type);
    if (filters?.layer) params.append('layer', filters.layer);
    if (filters?.status) params.append('status', filters.status);
    const { data } = await api.get(`/architecture/components?${params.toString()}`);
    return data;
  },
  getComponent: async (id: number): Promise<ArchitectureComponent> => {
    const { data } = await api.get(`/architecture/components/${id}`);
    return data;
  },
  createComponent: async (componentData: ArchitectureComponentCreate): Promise<ArchitectureComponent> => {
    const { data } = await api.post('/architecture/components', componentData);
    return data;
  },
  updateComponent: async (id: number, componentData: ArchitectureComponentUpdate): Promise<ArchitectureComponent> => {
    const { data } = await api.put(`/architecture/components/${id}`, componentData);
    return data;
  },
  deleteComponent: async (id: number): Promise<void> => {
    await api.delete(`/architecture/components/${id}`);
  },
  // 연결
  getConnections: async (): Promise<ArchitectureConnection[]> => {
    const { data } = await api.get('/architecture/connections');
    return data;
  },
  createConnection: async (connectionData: ArchitectureConnectionCreate): Promise<ArchitectureConnection> => {
    const { data } = await api.post('/architecture/connections', connectionData);
    return data;
  },
  updateConnection: async (id: number, connectionData: ArchitectureConnectionUpdate): Promise<ArchitectureConnection> => {
    const { data } = await api.put(`/architecture/connections/${id}`, connectionData);
    return data;
  },
  deleteConnection: async (id: number): Promise<void> => {
    await api.delete(`/architecture/connections/${id}`);
  },
  // 일괄 저장
  batchSave: async (components: ArchitectureComponentCreate[], connections: ArchitectureConnectionCreate[]): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post('/architecture/batch-save', { components, connections });
    return data;
  },
};

// Traceability Matrix (추적성 매트릭스)
import type { TraceabilityMatrixResponse, StyleGuide, StyleGuideCreate, StyleGuideUpdate, DesignAsset, DesignAssetCreate, DesignAssetUpdate, PublishingGuide, PublishingGuideCreate, PublishingGuideUpdate } from '@/types';

export const traceabilityApi = {
  getMatrix: async (): Promise<TraceabilityMatrixResponse> => {
    const { data } = await api.get('/traceability-matrix');
    return data;
  },
};

// Style Guides (웹스타일가이드)
export const styleGuidesApi = {
  getAll: async (filters?: { category?: string; status?: string }): Promise<StyleGuide[]> => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    const { data } = await api.get(`/style-guides?${params.toString()}`);
    return data;
  },
  getById: async (id: number): Promise<StyleGuide> => {
    const { data } = await api.get(`/style-guides/${id}`);
    return data;
  },
  create: async (guide: StyleGuideCreate): Promise<StyleGuide> => {
    const { data } = await api.post('/style-guides', guide);
    return data;
  },
  update: async (id: number, guide: StyleGuideUpdate): Promise<StyleGuide> => {
    const { data } = await api.put(`/style-guides/${id}`, guide);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/style-guides/${id}`);
  },
};

// Design Assets (디자인 산출물)
export const designAssetsApi = {
  getAll: async (filters?: { category?: string; asset_type?: string; status?: string }): Promise<DesignAsset[]> => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.asset_type) params.append('asset_type', filters.asset_type);
    if (filters?.status) params.append('status', filters.status);
    const { data } = await api.get(`/design-assets?${params.toString()}`);
    return data;
  },
  getById: async (id: number): Promise<DesignAsset> => {
    const { data } = await api.get(`/design-assets/${id}`);
    return data;
  },
  create: async (asset: DesignAssetCreate): Promise<DesignAsset> => {
    const { data } = await api.post('/design-assets', asset);
    return data;
  },
  update: async (id: number, asset: DesignAssetUpdate): Promise<DesignAsset> => {
    const { data } = await api.put(`/design-assets/${id}`, asset);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/design-assets/${id}`);
  },
};

// Publishing Guides (퍼블리싱 가이드)
export const publishingGuidesApi = {
  getAll: async (filters?: { category?: string; status?: string }): Promise<PublishingGuide[]> => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    const { data } = await api.get(`/publishing-guides?${params.toString()}`);
    return data;
  },
  getById: async (id: number): Promise<PublishingGuide> => {
    const { data } = await api.get(`/publishing-guides/${id}`);
    return data;
  },
  create: async (guide: PublishingGuideCreate): Promise<PublishingGuide> => {
    const { data } = await api.post('/publishing-guides', guide);
    return data;
  },
  update: async (id: number, guide: PublishingGuideUpdate): Promise<PublishingGuide> => {
    const { data } = await api.put(`/publishing-guides/${id}`, guide);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/publishing-guides/${id}`);
  },
};

// Accessibility Check (접근성 검증)
export interface AccessibilityIssue {
  rule_id: string;
  rule_name: string;
  category: string;
  level: 'error' | 'warning' | 'notice';
  message: string;
  element?: string;
  line?: number;
  wcag?: string;
  kwcag?: string;
}

export interface AccessibilityResult {
  url?: string;
  total_issues: number;
  errors: number;
  warnings: number;
  notices: number;
  passed: number;
  issues: AccessibilityIssue[];
  passed_checks: string[];
  checked_at: string;
  summary: {
    by_category: Record<string, { errors: number; warnings: number; notices: number }>;
    elements_checked: {
      images: number;
      headings: number;
      forms: number;
      links: number;
      tables: number;
      buttons: number;
    };
  };
}

export interface AccessibilityRule {
  id: string;
  name: string;
  category: string;
  description: string;
}

export interface AccessibilityHistory {
  id: number;
  check_type: 'url' | 'code' | 'file';
  target_name: string;
  target_preview?: string;
  errors: number;
  warnings: number;
  notices: number;
  passed: number;
  total_issues: number;
  result_json?: string;
  checked_by?: string;
  created_at: string;
}

export interface AccessibilityHistoryCreate {
  check_type: 'url' | 'code' | 'file';
  target_name: string;
  target_preview?: string;
  errors: number;
  warnings: number;
  notices: number;
  passed: number;
  total_issues: number;
  result_json?: string;
  checked_by?: string;
}

export const accessibilityApi = {
  check: async (params: { url?: string; html_code?: string }): Promise<AccessibilityResult> => {
    const { data } = await api.post('/accessibility/check', params);
    return data;
  },
  getRules: async (): Promise<{ standard: string; rules: AccessibilityRule[] }> => {
    const { data } = await api.get('/accessibility/rules');
    return data;
  },
  // 히스토리 API
  getHistory: async (): Promise<AccessibilityHistory[]> => {
    const { data } = await api.get('/accessibility/history');
    return data;
  },
  getHistoryDetail: async (id: number): Promise<AccessibilityHistory> => {
    const { data } = await api.get(`/accessibility/history/${id}`);
    return data;
  },
  createHistory: async (params: AccessibilityHistoryCreate): Promise<AccessibilityHistory> => {
    const { data } = await api.post('/accessibility/history', params);
    return data;
  },
  deleteHistory: async (id: number): Promise<void> => {
    await api.delete(`/accessibility/history/${id}`);
  },
};

// Dev Environments (개발환경 설정)
export interface DevEnvironment {
  id: number;
  env_id: string;
  language: string;
  language_version: string | null;
  framework: string | null;
  framework_version: string | null;
  build_tool: string | null;
  build_tool_version: string | null;
  package_manager: string | null;
  ide: string | null;
  ide_version: string | null;
  code_convention: string | null;
  linter: string | null;
  formatter: string | null;
  test_framework: string | null;
  coverage_tool: string | null;
  runtime: string | null;
  app_server: string | null;
  description: string | null;
  config_files: string | null;
  environment_variables: string | null;
  is_primary: boolean;
  status: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface DevEnvironmentCreate {
  language: string;
  language_version?: string | null;
  framework?: string | null;
  framework_version?: string | null;
  build_tool?: string | null;
  build_tool_version?: string | null;
  package_manager?: string | null;
  ide?: string | null;
  ide_version?: string | null;
  code_convention?: string | null;
  linter?: string | null;
  formatter?: string | null;
  test_framework?: string | null;
  coverage_tool?: string | null;
  runtime?: string | null;
  app_server?: string | null;
  description?: string | null;
  config_files?: string | null;
  environment_variables?: string | null;
  is_primary?: boolean;
  status?: string;
}

export interface DevEnvironmentPreset {
  language_versions: string[];
  frameworks: string[];
  build_tools: string[];
  package_managers: string[];
  ides: string[];
  code_conventions: string[];
  linters: string[];
  formatters: string[];
  test_frameworks: string[];
  coverage_tools: string[];
  runtimes: string[];
  app_servers: string[];
}

export const devEnvironmentsApi = {
  getAll: async (): Promise<DevEnvironment[]> => {
    const { data } = await api.get('/dev-environments');
    return data;
  },
  getById: async (id: number): Promise<DevEnvironment> => {
    const { data } = await api.get(`/dev-environments/${id}`);
    return data;
  },
  create: async (params: DevEnvironmentCreate): Promise<DevEnvironment> => {
    const { data } = await api.post('/dev-environments', params);
    return data;
  },
  update: async (id: number, params: Partial<DevEnvironmentCreate>): Promise<DevEnvironment> => {
    const { data } = await api.put(`/dev-environments/${id}`, params);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/dev-environments/${id}`);
  },
  // 프리셋 관련
  getLanguages: async (): Promise<string[]> => {
    const { data } = await api.get('/dev-environments/languages');
    return data;
  },
  getPresets: async (): Promise<Record<string, DevEnvironmentPreset>> => {
    const { data } = await api.get('/dev-environments/presets');
    return data;
  },
  getPresetByLanguage: async (language: string): Promise<DevEnvironmentPreset> => {
    const { data } = await api.get(`/dev-environments/presets/${language}`);
    return data;
  },
};

// 카테고리 관리 API
import type {
  Category,
  CategoryCreate,
  CategoryUpdate,
  CategoryWithItems,
  CategoryItem,
  CategoryItemCreate,
  CategoryItemUpdate,
  CommonModule,
  CommonModuleCreate,
  CommonModuleUpdate,
} from '@/types';

export const categoriesApi = {
  // 카테고리
  getAll: async (groupName?: string): Promise<CategoryWithItems[]> => {
    const params = groupName ? { group_name: groupName } : {};
    const { data } = await api.get('/categories', { params });
    return data;
  },
  getGroups: async (): Promise<string[]> => {
    const { data } = await api.get('/categories/groups');
    return data;
  },
  getByCode: async (code: string): Promise<CategoryWithItems> => {
    const { data } = await api.get(`/categories/by-code/${code}`);
    return data;
  },
  getById: async (id: number): Promise<CategoryWithItems> => {
    const { data } = await api.get(`/categories/${id}`);
    return data;
  },
  create: async (params: CategoryCreate): Promise<Category> => {
    const { data } = await api.post('/categories', params);
    return data;
  },
  update: async (id: number, params: CategoryUpdate): Promise<Category> => {
    const { data } = await api.put(`/categories/${id}`, params);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/categories/${id}`);
  },

  // 카테고리 항목 트리 조회 (parent-child 구조)
  getItemsTree: async (categoryCode: string): Promise<Array<{
    id: number;
    code: string;
    name: string;
    color: string | null;
    order_index: number;
    children: Array<{
      id: number;
      code: string;
      name: string;
      color: string | null;
      order_index: number;
      parent_id: number;
    }>;
  }>> => {
    const { data } = await api.get(`/category-items/tree/${categoryCode}`);
    return data;
  },

  // 카테고리 항목
  getItems: async (categoryCode?: string, categoryId?: number): Promise<CategoryItem[]> => {
    const params: Record<string, string | number> = {};
    if (categoryCode) params.category_code = categoryCode;
    if (categoryId) params.category_id = categoryId;
    const { data } = await api.get('/category-items', { params });
    return data;
  },
  getItem: async (id: number): Promise<CategoryItem> => {
    const { data } = await api.get(`/category-items/${id}`);
    return data;
  },
  createItem: async (params: CategoryItemCreate): Promise<CategoryItem> => {
    const { data } = await api.post('/category-items', params);
    return data;
  },
  createItemsBulk: async (params: CategoryItemCreate[]): Promise<CategoryItem[]> => {
    const { data } = await api.post('/category-items/bulk', params);
    return data;
  },
  updateItem: async (id: number, params: CategoryItemUpdate): Promise<CategoryItem> => {
    const { data } = await api.put(`/category-items/${id}`, params);
    return data;
  },
  deleteItem: async (id: number): Promise<void> => {
    await api.delete(`/category-items/${id}`);
  },
};

// 공통 모듈 가이드 API
export const commonModulesApi = {
  getAll: async (category?: string, status?: string): Promise<CommonModule[]> => {
    const params: Record<string, string> = {};
    if (category) params.category = category;
    if (status) params.status = status;
    const { data } = await api.get('/common-modules', { params });
    return data;
  },
  getById: async (id: number): Promise<CommonModule> => {
    const { data } = await api.get(`/common-modules/${id}`);
    return data;
  },
  create: async (params: CommonModuleCreate): Promise<CommonModule> => {
    const { data } = await api.post('/common-modules', params);
    return data;
  },
  update: async (id: number, params: CommonModuleUpdate): Promise<CommonModule> => {
    const { data } = await api.put(`/common-modules/${id}`, params);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/common-modules/${id}`);
  },
  getCategories: async (): Promise<string[]> => {
    const { data } = await api.get('/common-modules/categories');
    return data;
  },
};

// 개발작업 API
export const devTasksApi = {
  getAll: async (status?: string, developer?: string, priority?: string): Promise<DevTask[]> => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (developer) params.developer = developer;
    if (priority) params.priority = priority;
    const { data } = await api.get('/dev-tasks', { params });
    return data;
  },
  getById: async (id: number): Promise<DevTask> => {
    const { data } = await api.get(`/dev-tasks/${id}`);
    return data;
  },
  create: async (params: DevTaskCreate): Promise<DevTask> => {
    const { data } = await api.post('/dev-tasks', params);
    return data;
  },
  update: async (id: number, params: DevTaskUpdate): Promise<DevTask> => {
    const { data } = await api.put(`/dev-tasks/${id}`, params);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/dev-tasks/${id}`);
  },
  generate: async (requirementIds?: number[]): Promise<DevTaskAutoGenerateResponse> => {
    const { data } = await api.post('/dev-tasks/generate', { requirement_ids: requirementIds });
    return data;
  },
  getWbsDates: async (): Promise<WbsDates> => {
    const { data } = await api.get('/dev-tasks/wbs-dates');
    return data;
  },
  getDevelopers: async (): Promise<string[]> => {
    const { data } = await api.get('/dev-tasks/developers');
    return data;
  },
};

// 시스템 설정 API
export interface SystemSetting {
  id: number;
  key: string;
  value: string | null;
  description: string | null;
  category: string | null;
  created_at: string;
  updated_at: string;
}

export interface SystemSettingCreate {
  key: string;
  value?: string | null;
  description?: string | null;
  category?: string | null;
}

export const settingsApi = {
  getAll: async (category?: string): Promise<SystemSetting[]> => {
    const params = category ? { category } : {};
    const { data } = await api.get('/settings', { params });
    return data;
  },
  getByKey: async (key: string): Promise<SystemSetting> => {
    const { data } = await api.get(`/settings/${key}`);
    return data;
  },
  upsert: async (key: string, params: SystemSettingCreate): Promise<SystemSetting> => {
    const { data } = await api.put(`/settings/${key}`, params);
    return data;
  },
  bulkUpsert: async (settings: SystemSettingCreate[]): Promise<SystemSetting[]> => {
    const { data } = await api.post('/settings/bulk', { settings });
    return data;
  },
  delete: async (key: string): Promise<void> => {
    await api.delete(`/settings/${key}`);
  },
  resetAllData: async (options: { keepUsers?: boolean; keepSettings?: boolean } = {}): Promise<{
    success: boolean;
    message: string;
    deleted_counts: Record<string, number | string>;
    keep_users: boolean;
    keep_settings: boolean;
  }> => {
    const { data } = await api.post('/system/reset-all-data', null, {
      params: {
        confirm: 'CONFIRM_RESET',
        keep_users: options.keepUsers ?? true,
        keep_settings: options.keepSettings ?? true,
      },
    });
    return data;
  },
};

// ==================== 산출물 매핑 관리 ====================

export interface StageArtifactMappingItem {
  id: number;
  stage_name: string;
  artifact_key: string;
  artifact_label: string;
  model_name: string;
  link: string;
  icon: string;
  done_status: string[] | null;
  wip_status: string[] | null;
  is_active: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export const stageArtifactMappingsApi = {
  getAll: async (params?: { stage_name?: string; is_active?: boolean }): Promise<StageArtifactMappingItem[]> => {
    const { data } = await api.get('/stage-artifact-mappings', { params });
    return data;
  },
  create: async (mapping: Omit<StageArtifactMappingItem, 'id' | 'created_at' | 'updated_at'>): Promise<StageArtifactMappingItem> => {
    const { data } = await api.post('/stage-artifact-mappings', mapping);
    return data;
  },
  update: async (id: number, mapping: Partial<StageArtifactMappingItem>): Promise<StageArtifactMappingItem> => {
    const { data } = await api.put(`/stage-artifact-mappings/${id}`, mapping);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/stage-artifact-mappings/${id}`);
  },
  resetDefaults: async (): Promise<{ message: string }> => {
    const { data } = await api.post('/stage-artifact-mappings/reset-defaults');
    return data;
  },
};

// ==================== 부문별 권한 관리 ====================

export interface DepartmentPermission {
  id: number;
  department: string;
  stage: string;
  permission_level: 'read_write' | 'read_only';
  description?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DepartmentPermissionCreate {
  department: string;
  stage: string;
  permission_level: 'read_write' | 'read_only';
  description?: string | null;
}

export interface DepartmentPermissionUpdate {
  permission_level?: 'read_write' | 'read_only';
  description?: string | null;
}

export interface PermissionCheckResponse {
  has_permission: boolean;
  user_role: string;
  user_email: string;
  department: string;
  stage: string;
  required_permission: string;
}

export const departmentPermissionsApi = {
  getAll: async (params?: { department?: string; stage?: string }): Promise<DepartmentPermission[]> => {
    const { data } = await api.get('/department-permissions', { params });
    return data;
  },
  getById: async (id: number): Promise<DepartmentPermission> => {
    const { data } = await api.get(`/department-permissions/${id}`);
    return data;
  },
  create: async (permission: DepartmentPermissionCreate): Promise<DepartmentPermission> => {
    const { data } = await api.post('/department-permissions', permission);
    return data;
  },
  update: async (id: number, permission: DepartmentPermissionUpdate): Promise<DepartmentPermission> => {
    const { data } = await api.put(`/department-permissions/${id}`, permission);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/department-permissions/${id}`);
  },
  getStages: async (): Promise<{ code: string; name: string }[]> => {
    const { data } = await api.get('/department-permissions/options/stages');
    return data;
  },
  getLevels: async (): Promise<{ code: string; name: string }[]> => {
    const { data } = await api.get('/department-permissions/options/levels');
    return data;
  },
  getDepartments: async (): Promise<{ code: string; name: string; color: string }[]> => {
    const { data } = await api.get('/department-permissions/options/departments');
    return data;
  },
  check: async (department: string, stage: string, permission: string = 'read'): Promise<PermissionCheckResponse> => {
    const { data } = await api.get('/department-permissions/check', {
      params: { department, stage, permission },
    });
    return data;
  },
};

// 코딩 컨벤션 API
export interface CodingConvention {
  id: number;
  convention_id: string;
  category: string;
  language: string | null;
  title: string;
  description: string | null;
  rule: string | null;
  good_example: string | null;
  bad_example: string | null;
  severity: string;
  status: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface CodingConventionCreate {
  category: string;
  language?: string | null;
  title: string;
  description?: string | null;
  rule?: string | null;
  good_example?: string | null;
  bad_example?: string | null;
  severity?: string;
  status?: string;
  order_index?: number;
}

export interface CodingConventionUpdate {
  category?: string;
  language?: string | null;
  title?: string;
  description?: string | null;
  rule?: string | null;
  good_example?: string | null;
  bad_example?: string | null;
  severity?: string;
  status?: string;
  order_index?: number;
}

export const codingConventionsApi = {
  getAll: async (category?: string, language?: string): Promise<CodingConvention[]> => {
    const params: Record<string, string> = {};
    if (category) params.category = category;
    if (language) params.language = language;
    const { data } = await api.get('/coding-conventions', { params });
    return data;
  },
  getCategories: async (): Promise<string[]> => {
    const { data } = await api.get('/coding-conventions/categories');
    return data;
  },
  getLanguages: async (): Promise<string[]> => {
    const { data } = await api.get('/coding-conventions/languages');
    return data;
  },
  getById: async (id: number): Promise<CodingConvention> => {
    const { data } = await api.get(`/coding-conventions/${id}`);
    return data;
  },
  create: async (params: CodingConventionCreate): Promise<CodingConvention> => {
    const { data } = await api.post('/coding-conventions', params);
    return data;
  },
  update: async (id: number, params: CodingConventionUpdate): Promise<CodingConvention> => {
    const { data } = await api.put(`/coding-conventions/${id}`, params);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/coding-conventions/${id}`);
  },
};

// 코드 문서 API
export interface CodeDocument {
  id: number;
  doc_id: string;
  file_path: string;
  file_name: string;
  language: string | null;
  file_content: string | null;
  github_repo: string | null;
  github_branch: string | null;
  github_sha: string | null;
  github_url: string | null;
  analysis_status: 'pending' | 'analyzing' | 'completed' | 'failed';
  analysis_result: string | null;  // JSON string
  analyzed_at: string | null;
  status: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface CodeDocumentListItem {
  id: number;
  doc_id: string;
  file_path: string;
  file_name: string;
  language: string | null;
  github_repo: string | null;
  github_branch: string | null;
  analysis_status: 'pending' | 'analyzing' | 'completed' | 'failed';
  status: string;
  analyzed_at: string | null;
  order_index: number;
  created_at: string;
}

export interface CodeDocumentAnalyzeRequest {
  doc_id?: number;
  file_path: string;
  file_name: string;
  file_content: string;
  language?: string;
  github_repo?: string;
  github_branch?: string;
  github_sha?: string;
  github_url?: string;
}

export const codeDocumentsApi = {
  getAll: async (language?: string, analysisStatus?: string): Promise<CodeDocumentListItem[]> => {
    const params: Record<string, string> = {};
    if (language) params.language = language;
    if (analysisStatus) params.analysis_status = analysisStatus;
    const { data } = await api.get('/code-documents', { params });
    return data;
  },
  getById: async (id: number): Promise<CodeDocument> => {
    const { data } = await api.get(`/code-documents/${id}`);
    return data;
  },
  analyze: async (request: CodeDocumentAnalyzeRequest): Promise<CodeDocument> => {
    const { data } = await api.post('/code-documents/analyze', request);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/code-documents/${id}`);
  },
};

// Storage API (Google Drive 연동)
export interface GdriveTestRequest {
  client_id: string;
  client_secret: string;
  refresh_token?: string;
  folder_id?: string;
}

export interface GdriveTestResponse {
  success: boolean;
  user?: string;
  folder_name?: string;
  storage_quota?: {
    limit?: string;
    usage?: string;
    usageInDrive?: string;
  };
  error?: string;
}

export interface StorageStatusResponse {
  current_provider: string;
  gdrive_configured: boolean;
  gdrive_enabled: boolean;
}

export const storageApi = {
  testGdrive: async (params: GdriveTestRequest): Promise<GdriveTestResponse> => {
    const { data } = await api.post('/storage/gdrive/test', params);
    return data;
  },
  getStatus: async (): Promise<StorageStatusResponse> => {
    const { data } = await api.get('/storage/status');
    return data;
  },
  initOAuth: async (params: { client_id: string; redirect_uri: string }): Promise<{ auth_url: string }> => {
    const { data } = await api.post('/storage/gdrive/oauth/init', params);
    return data;
  },
  oauthCallback: async (params: { code: string; client_id: string; client_secret: string; redirect_uri: string }): Promise<{ success: boolean; refresh_token: string }> => {
    const { data } = await api.post('/storage/gdrive/oauth/callback', params);
    return data;
  },
  getFilesystemStatus: async (): Promise<{
    status: string;
    provider: string;
    base_path: string;
    total_files: number;
    total_size_mb: number;
    disk_total_gb: number;
    disk_free_gb: number;
    disk_usage_percent: number;
  }> => {
    const { data } = await api.get('/storage/filesystem/status');
    return data;
  },
};

// Common Codes API (공통코드)
export interface CommonCode {
  id: number;
  code_group: string;
  code: string;
  name: string;
  description?: string;
  code_type: 'major' | 'minor';
  parent_id?: number;
  order_index: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  children?: CommonCode[];
}

export interface CommonCodeCreate {
  code_group: string;
  code: string;
  name: string;
  description?: string;
  code_type: 'major' | 'minor';
  parent_id?: number;
  order_index?: number;
  is_active?: boolean;
}

export interface CommonCodeUpdate {
  code_group?: string;
  code?: string;
  name?: string;
  description?: string;
  code_type?: 'major' | 'minor';
  parent_id?: number;
  order_index?: number;
  is_active?: boolean;
}

export interface CommonCodeGroupStats {
  code_group: string;
  total_count: number;
  major_count: number;
  minor_count: number;
}

export const commonCodesApi = {
  getAll: async (filters?: { code_group?: string; code_type?: string; is_active?: boolean }): Promise<CommonCode[]> => {
    const params = new URLSearchParams();
    if (filters?.code_group) params.append('code_group', filters.code_group);
    if (filters?.code_type) params.append('code_type', filters.code_type);
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    const { data } = await api.get(`/common-codes?${params.toString()}`);
    return data;
  },
  getTree: async (codeGroup: string): Promise<CommonCode[]> => {
    const { data } = await api.get(`/common-codes/tree/${codeGroup}`);
    return data;
  },
  getGroups: async (): Promise<CommonCodeGroupStats[]> => {
    const { data } = await api.get('/common-codes/groups');
    return data;
  },
  getById: async (id: number): Promise<CommonCode> => {
    const { data } = await api.get(`/common-codes/${id}`);
    return data;
  },
  create: async (code: CommonCodeCreate): Promise<CommonCode> => {
    const { data } = await api.post('/common-codes', code);
    return data;
  },
  createBulk: async (codes: CommonCodeCreate[]): Promise<CommonCode[]> => {
    const { data } = await api.post('/common-codes/bulk', { codes });
    return data;
  },
  update: async (id: number, code: CommonCodeUpdate): Promise<CommonCode> => {
    const { data } = await api.put(`/common-codes/${id}`, code);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/common-codes/${id}`);
  },
};

// ============ 프로젝트 API ============

import type {
  Project,
  ProjectCreate,
  ProjectUpdate,
  ProjectStats,
} from '@/types';

export const projectsApi = {
  getAll: async (status?: string): Promise<Project[]> => {
    const params = status ? { status } : {};
    const { data } = await api.get('/projects', { params });
    return data;
  },
  getById: async (id: number): Promise<Project> => {
    const { data } = await api.get(`/projects/${id}`);
    return data;
  },
  getByCode: async (code: string): Promise<Project> => {
    const { data } = await api.get(`/projects/by-code/${code}`);
    return data;
  },
  create: async (project: ProjectCreate): Promise<Project> => {
    const { data } = await api.post('/projects', project);
    return data;
  },
  update: async (id: number, project: ProjectUpdate): Promise<Project> => {
    const { data } = await api.put(`/projects/${id}`, project);
    return data;
  },
  delete: async (id: number, force: boolean = false): Promise<{ success: boolean; message: string; deleted_counts?: Record<string, number> }> => {
    const { data } = await api.delete(`/projects/${id}?force=${force}`);
    return data;
  },
  getStats: async (id: number): Promise<ProjectStats> => {
    const { data } = await api.get(`/projects/${id}/stats`);
    return data;
  },
};

// ============ 테스트 케이스 API ============

import type {
  TestCase,
  TestCaseListItem,
  TestCaseCreate,
  TestCaseUpdate,
  TestExecution,
  TestExecutionCreate,
  TestCaseStats,
  AITestCasePreviewRequest,
  AITestCasePreviewResponse,
  AITestCaseSuggestion,
  TestScenario,
  TestScenarioListItem,
  TestScenarioCreate,
  TestScenarioUpdate,
  AIScenarioPreviewRequest,
  AIScenarioPreviewResponse,
} from '@/types';

export const testCasesApi = {
  // 목록 조회
  getAll: async (params?: { project_id?: number; category?: string; status?: string }): Promise<TestCaseListItem[]> => {
    const { data } = await api.get('/test-cases', { params });
    return data;
  },

  // 상세 조회
  getById: async (id: number): Promise<TestCase> => {
    const { data } = await api.get(`/test-cases/${id}`);
    return data;
  },

  // 생성
  create: async (testCase: TestCaseCreate): Promise<TestCase> => {
    const { data } = await api.post('/test-cases', testCase);
    return data;
  },

  // 수정
  update: async (id: number, testCase: TestCaseUpdate): Promise<TestCase> => {
    const { data } = await api.put(`/test-cases/${id}`, testCase);
    return data;
  },

  // 삭제
  delete: async (id: number): Promise<void> => {
    await api.delete(`/test-cases/${id}`);
  },

  // 통계
  getStats: async (projectId?: number): Promise<TestCaseStats> => {
    const params = projectId ? { project_id: projectId } : {};
    const { data } = await api.get('/test-cases/stats', { params });
    return data;
  },

  // 실행 결과 조회
  getExecutions: async (testCaseId: number): Promise<TestExecution[]> => {
    const { data } = await api.get(`/test-cases/${testCaseId}/executions`);
    return data;
  },

  // 실행 결과 생성
  createExecution: async (execution: TestExecutionCreate): Promise<TestExecution> => {
    const { data } = await api.post('/test-executions', execution);
    return data;
  },

  // AI 테스트 케이스 미리보기 (AI 호출은 오래 걸릴 수 있으므로 타임아웃 연장)
  aiPreview: async (request: AITestCasePreviewRequest): Promise<AITestCasePreviewResponse> => {
    const { data } = await api.post('/test-cases/ai/preview', request, { timeout: 180000 });
    return data;
  },

  // AI 제안 저장
  aiSave: async (request: {
    source_type: 'requirement' | 'screen' | 'api' | 'scenario';
    source_id: number;
    suggestions: AITestCaseSuggestion[];
    selected_indices: number[];
  }): Promise<{ saved_count: number; test_cases: TestCase[] }> => {
    const { data } = await api.post('/test-cases/ai/save', request);
    return data;
  },

  // 질문별 실행 상태 업데이트
  updateStepExecution: async (
    testCaseId: number,
    stepData: {
      step: number;
      status: string;
      assignee?: string;
      error_text?: string;
    }
  ): Promise<{ message: string; step: number; status: string }> => {
    const { data } = await api.put(`/test-cases/${testCaseId}/step-execution`, stepData);
    return data;
  },
};

// ============ 테스트 시나리오 API ============

export const testScenariosApi = {
  // 목록 조회
  getAll: async (params?: { project_id?: number; scenario_type?: string; status?: string }): Promise<TestScenarioListItem[]> => {
    const { data } = await api.get('/test-scenarios', { params });
    return data;
  },

  // 상세 조회
  getById: async (id: number): Promise<TestScenario> => {
    const { data } = await api.get(`/test-scenarios/${id}`);
    return data;
  },

  // 생성
  create: async (scenario: TestScenarioCreate): Promise<TestScenario> => {
    const { data } = await api.post('/test-scenarios', scenario);
    return data;
  },

  // 수정
  update: async (id: number, scenario: TestScenarioUpdate): Promise<TestScenario> => {
    const { data } = await api.put(`/test-scenarios/${id}`, scenario);
    return data;
  },

  // 삭제
  delete: async (id: number): Promise<void> => {
    await api.delete(`/test-scenarios/${id}`);
  },

  // AI 시나리오 미리보기 (AI 호출은 오래 걸릴 수 있으므로 타임아웃 연장)
  aiPreview: async (request: AIScenarioPreviewRequest): Promise<AIScenarioPreviewResponse> => {
    const { data } = await api.post('/test-scenarios/ai/preview', request, { timeout: 180000 });
    return data;
  },
};

// ============ 테스트 실행 API ============

export interface TestRunItem {
  id: number;
  test_run_id: number;
  test_case_id: number;
  result: string; // 대기/Pass/Fail/Block/Skip
  actual_result?: string;
  defect_description?: string;
  executed_at?: string;
  executed_by?: string;
  order_index: number;
  test_case?: TestCase;
}

export interface TestRun {
  id: number;
  project_id?: number;
  run_id: string;
  run_name: string;
  description?: string;
  test_type: string; // 단위테스트/통합테스트/시스템테스트/회귀테스트
  environment?: string; // 개발/스테이징/운영
  status: string; // 대기/진행중/완료
  tester?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  items: TestRunItem[];
  total_count: number;
  pass_count: number;
  fail_count: number;
  pending_count: number;
}

export interface TestRunListItem {
  id: number;
  project_id?: number;
  run_id: string;
  run_name: string;
  description?: string;
  test_type: string;
  environment?: string;
  status: string;
  tester?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  total_count: number;
  pass_count: number;
  fail_count: number;
  pending_count: number;
}

export interface TestRunCreate {
  project_id?: number;
  run_name: string;
  description?: string;
  test_type?: string;
  environment?: string;
  status?: string;
  tester?: string;
  test_case_ids?: number[];
}

export interface TestRunUpdate {
  run_name?: string;
  description?: string;
  test_type?: string;
  environment?: string;
  status?: string;
  tester?: string;
  started_at?: string;
  completed_at?: string;
}

export interface TestRunItemUpdate {
  result?: string;
  actual_result?: string;
  defect_description?: string;
  executed_at?: string;
  executed_by?: string;
}

export const testRunsApi = {
  // 목록 조회
  getAll: async (params?: { project_id?: number; test_type?: string; status?: string; tester?: string }): Promise<TestRunListItem[]> => {
    const { data } = await api.get('/test-runs', { params });
    return data;
  },

  // 상세 조회
  getById: async (id: number): Promise<TestRun> => {
    const { data } = await api.get(`/test-runs/${id}`);
    return data;
  },

  // 생성
  create: async (testRun: TestRunCreate): Promise<TestRun> => {
    const { data } = await api.post('/test-runs', testRun);
    return data;
  },

  // 수정
  update: async (id: number, testRun: TestRunUpdate): Promise<TestRun> => {
    const { data } = await api.put(`/test-runs/${id}`, testRun);
    return data;
  },

  // 삭제
  delete: async (id: number): Promise<void> => {
    await api.delete(`/test-runs/${id}`);
  },

  // 케이스 추가
  addCases: async (id: number, testCaseIds: number[]): Promise<TestRun> => {
    const { data } = await api.post(`/test-runs/${id}/cases`, { test_case_ids: testCaseIds });
    return data;
  },

  // 케이스 제거
  removeCase: async (id: number, itemId: number): Promise<void> => {
    await api.delete(`/test-runs/${id}/cases/${itemId}`);
  },

  // 항목 결과 업데이트
  updateItem: async (id: number, itemId: number, update: TestRunItemUpdate): Promise<TestRunItem> => {
    const { data } = await api.put(`/test-runs/${id}/items/${itemId}`, update);
    return data;
  },

  // 일괄 업데이트
  bulkUpdateItems: async (id: number, items: Array<{ id: number; result?: string; actual_result?: string; defect_description?: string }>): Promise<{ updated_count: number }> => {
    const { data } = await api.put(`/test-runs/${id}/bulk-update`, { items });
    return data;
  },

  // 테스트 시작
  start: async (id: number): Promise<TestRun> => {
    const { data } = await api.post(`/test-runs/${id}/start`);
    return data;
  },

  // 테스트 완료
  complete: async (id: number): Promise<TestRun> => {
    const { data } = await api.post(`/test-runs/${id}/complete`);
    return data;
  },
};

// ============ 이행 관련 API ============

// 이행계획서
export interface DeploymentPlan {
  id: number;
  project_id?: number;
  plan_id: string;
  title: string;
  version: string;
  description?: string;
  planned_date?: string;
  wbs_task_id?: number;
  checklist: Array<{
    category: string;
    item: string;
    responsible?: string;
    checked: boolean;
    notes?: string;
  }>;
  rollback_plan: Array<{
    step: number;
    condition: string;
    action: string;
    responsible?: string;
    estimated_time?: string;
  }>;
  approver?: string;
  approved_at?: string;
  status: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface DeploymentPlanCreate {
  project_id?: number;
  title: string;
  version?: string;
  description?: string;
  planned_date?: string;
  wbs_task_id?: number;
  checklist?: DeploymentPlan['checklist'];
  rollback_plan?: DeploymentPlan['rollback_plan'];
  approver?: string;
  approved_at?: string;
  status?: string;
}

export interface DeploymentPlanUpdate {
  title?: string;
  version?: string;
  description?: string;
  planned_date?: string;
  wbs_task_id?: number;
  checklist?: DeploymentPlan['checklist'];
  rollback_plan?: DeploymentPlan['rollback_plan'];
  approver?: string;
  approved_at?: string;
  status?: string;
}

export const deploymentPlansApi = {
  getAll: async (params?: { project_id?: number; status?: string }): Promise<DeploymentPlan[]> => {
    const { data } = await api.get('/deployment-plans', { params });
    return data;
  },
  getById: async (id: number): Promise<DeploymentPlan> => {
    const { data } = await api.get(`/deployment-plans/${id}`);
    return data;
  },
  create: async (plan: DeploymentPlanCreate): Promise<DeploymentPlan> => {
    const { data } = await api.post('/deployment-plans', plan);
    return data;
  },
  update: async (id: number, plan: DeploymentPlanUpdate): Promise<DeploymentPlan> => {
    const { data } = await api.put(`/deployment-plans/${id}`, plan);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/deployment-plans/${id}`);
  },
};

// 환경구성
export interface DeploymentEnvironment {
  id: number;
  project_id?: number;
  env_id: string;
  name: string;
  env_type: string;
  description?: string;
  servers: Array<{
    name: string;
    type: string;
    os?: string;
    cpu?: string;
    memory?: string;
    disk?: string;
    ip?: string;
    status?: string;
  }>;
  network: {
    domain?: string;
    ssl?: boolean;
    firewall_rules?: Array<{ port: number; protocol: string; description?: string }>;
    load_balancer?: { type: string; config?: string };
  };
  integrations: Array<{
    name: string;
    type: string;
    endpoint?: string;
    auth_type?: string;
    status?: string;
  }>;
  status: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface DeploymentEnvironmentCreate {
  project_id?: number;
  name: string;
  env_type: string;
  description?: string;
  servers?: DeploymentEnvironment['servers'];
  network?: DeploymentEnvironment['network'];
  integrations?: DeploymentEnvironment['integrations'];
  status?: string;
}

export interface DeploymentEnvironmentUpdate {
  name?: string;
  env_type?: string;
  description?: string;
  servers?: DeploymentEnvironment['servers'];
  network?: DeploymentEnvironment['network'];
  integrations?: DeploymentEnvironment['integrations'];
  status?: string;
}

export const deploymentEnvironmentsApi = {
  getAll: async (params?: { project_id?: number; env_type?: string }): Promise<DeploymentEnvironment[]> => {
    const { data } = await api.get('/deployment-environments', { params });
    return data;
  },
  getById: async (id: number): Promise<DeploymentEnvironment> => {
    const { data } = await api.get(`/deployment-environments/${id}`);
    return data;
  },
  create: async (env: DeploymentEnvironmentCreate): Promise<DeploymentEnvironment> => {
    const { data } = await api.post('/deployment-environments', env);
    return data;
  },
  update: async (id: number, env: DeploymentEnvironmentUpdate): Promise<DeploymentEnvironment> => {
    const { data } = await api.put(`/deployment-environments/${id}`, env);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/deployment-environments/${id}`);
  },
};

// 데이터이행
export interface DataMigration {
  id: number;
  project_id?: number;
  migration_id: string;
  name: string;
  source_system?: string;
  target_system?: string;
  description?: string;
  mapping: Array<{
    source_table: string;
    target_table: string;
    columns?: Array<{ source: string; target: string; transform?: string }>;
    transform_rule?: string;
  }>;
  script_type?: string;
  script_content?: string;
  verification: Array<{
    type: string;
    expected?: number;
    actual?: number;
    result?: string;
    verified_at?: string;
  }>;
  executed_at?: string;
  executed_by?: string;
  execution_log?: string;
  status: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface DataMigrationCreate {
  project_id?: number;
  name: string;
  source_system?: string;
  target_system?: string;
  description?: string;
  mapping?: DataMigration['mapping'];
  script_type?: string;
  script_content?: string;
  verification?: DataMigration['verification'];
  status?: string;
}

export interface DataMigrationUpdate {
  name?: string;
  source_system?: string;
  target_system?: string;
  description?: string;
  mapping?: DataMigration['mapping'];
  script_type?: string;
  script_content?: string;
  verification?: DataMigration['verification'];
  executed_at?: string;
  executed_by?: string;
  execution_log?: string;
  status?: string;
}

export const dataMigrationsApi = {
  getAll: async (params?: { project_id?: number; status?: string }): Promise<DataMigration[]> => {
    const { data } = await api.get('/data-migrations', { params });
    return data;
  },
  getById: async (id: number): Promise<DataMigration> => {
    const { data } = await api.get(`/data-migrations/${id}`);
    return data;
  },
  create: async (migration: DataMigrationCreate): Promise<DataMigration> => {
    const { data } = await api.post('/data-migrations', migration);
    return data;
  },
  update: async (id: number, migration: DataMigrationUpdate): Promise<DataMigration> => {
    const { data } = await api.put(`/data-migrations/${id}`, migration);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/data-migrations/${id}`);
  },
};

// 배포관리
export interface DeploymentHistory {
  id: number;
  project_id?: number;
  deploy_id: string;
  title: string;
  deploy_type: string;
  environment: string;
  version?: string;
  description?: string;
  procedures: Array<{
    step: number;
    action: string;
    command?: string;
    expected_result?: string;
    actual_result?: string;
    status?: string;
  }>;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  deployer?: string;
  approver?: string;
  result?: string;
  result_note?: string;
  status: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface DeploymentHistoryCreate {
  project_id?: number;
  title: string;
  deploy_type: string;
  environment: string;
  version?: string;
  description?: string;
  procedures?: DeploymentHistory['procedures'];
  scheduled_at?: string;
  deployer?: string;
  approver?: string;
  status?: string;
}

export interface DeploymentHistoryUpdate {
  title?: string;
  deploy_type?: string;
  environment?: string;
  version?: string;
  description?: string;
  procedures?: DeploymentHistory['procedures'];
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  deployer?: string;
  approver?: string;
  result?: string;
  result_note?: string;
  status?: string;
}

export const deploymentHistoriesApi = {
  getAll: async (params?: { project_id?: number; environment?: string; status?: string }): Promise<DeploymentHistory[]> => {
    const { data } = await api.get('/deployment-histories', { params });
    return data;
  },
  getById: async (id: number): Promise<DeploymentHistory> => {
    const { data } = await api.get(`/deployment-histories/${id}`);
    return data;
  },
  create: async (history: DeploymentHistoryCreate): Promise<DeploymentHistory> => {
    const { data } = await api.post('/deployment-histories', history);
    return data;
  },
  update: async (id: number, history: DeploymentHistoryUpdate): Promise<DeploymentHistory> => {
    const { data } = await api.put(`/deployment-histories/${id}`, history);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/deployment-histories/${id}`);
  },
  start: async (id: number): Promise<DeploymentHistory> => {
    const { data } = await api.post(`/deployment-histories/${id}/start`);
    return data;
  },
  complete: async (id: number, result: string = '성공', resultNote?: string): Promise<DeploymentHistory> => {
    const { data } = await api.post(`/deployment-histories/${id}/complete`, null, {
      params: { result, result_note: resultNote },
    });
    return data;
  },
};

// 운영인수인계
export interface OperationHandover {
  id: number;
  project_id?: number;
  handover_id: string;
  title: string;
  category: string;
  description?: string;
  manual_content: {
    sections?: Array<{
      title: string;
      content: string;
      attachments?: string[];
    }>;
  };
  incident_responses: Array<{
    symptom: string;
    cause?: string;
    solution: string;
    prevention?: string;
    priority?: string;
  }>;
  contacts: Array<{
    role: string;
    name: string;
    contact?: string;
    email?: string;
    responsibilities?: string;
  }>;
  handover_date?: string;
  from_team?: string;
  to_team?: string;
  status: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface OperationHandoverCreate {
  project_id?: number;
  title: string;
  category: string;
  description?: string;
  manual_content?: OperationHandover['manual_content'];
  incident_responses?: OperationHandover['incident_responses'];
  contacts?: OperationHandover['contacts'];
  handover_date?: string;
  from_team?: string;
  to_team?: string;
  status?: string;
}

export interface OperationHandoverUpdate {
  title?: string;
  category?: string;
  description?: string;
  manual_content?: OperationHandover['manual_content'];
  incident_responses?: OperationHandover['incident_responses'];
  contacts?: OperationHandover['contacts'];
  handover_date?: string;
  from_team?: string;
  to_team?: string;
  status?: string;
}

export const operationHandoversApi = {
  getAll: async (params?: { project_id?: number; category?: string }): Promise<OperationHandover[]> => {
    const { data } = await api.get('/operation-handovers', { params });
    return data;
  },
  getById: async (id: number): Promise<OperationHandover> => {
    const { data } = await api.get(`/operation-handovers/${id}`);
    return data;
  },
  create: async (handover: OperationHandoverCreate): Promise<OperationHandover> => {
    const { data } = await api.post('/operation-handovers', handover);
    return data;
  },
  update: async (id: number, handover: OperationHandoverUpdate): Promise<OperationHandover> => {
    const { data } = await api.put(`/operation-handovers/${id}`, handover);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/operation-handovers/${id}`);
  },
};

// WBS 이행일정
export interface WbsDeploymentTask {
  id: number;
  task_name: string;
  depth: number;
  start_date?: string;
  end_date?: string;
  status: string;
  progress: number;
  manager?: string;
}

export const wbsDeploymentApi = {
  getSchedule: async (projectId?: number): Promise<WbsDeploymentTask[]> => {
    const params = projectId ? { project_id: projectId } : {};
    const { data } = await api.get('/wbs/deployment-schedule', { params });
    return data;
  },
};

// 프로젝트 산출물
export interface ProjectDeliverable {
  id: number;
  deliverable_id: string;
  name: string;
  description?: string;
  stage: string;
  category?: string;
  source_type: string;
  source_table?: string;
  source_id?: number;
  content?: {
    cover?: {
      title: string;
      subtitle: string;
      project_info?: Record<string, string>;
    };
    overview?: string;
    toc?: string[];
    source_count?: number;
  };
  file_name?: string;
  file_type?: string;
  version: string;
  status: string;
  is_required: boolean;
  is_generated: boolean;
  has_file?: boolean;
  gdrive_url?: string;
  generated_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectDeliverableCreate {
  project_id?: number;
  name: string;
  description?: string;
  stage: string;
  category?: string;
  source_type: string;
  source_table?: string;
  source_id?: number;
  content?: ProjectDeliverable['content'];
  file_data?: string;
  file_name?: string;
  file_type?: string;
  version?: string;
  status?: string;
  is_required?: boolean;
  is_generated?: boolean;
}

export interface ProjectDeliverableUpdate {
  name?: string;
  description?: string;
  stage?: string;
  category?: string;
  source_type?: string;
  content?: ProjectDeliverable['content'];
  file_data?: string;
  file_name?: string;
  file_type?: string;
  version?: string;
  status?: string;
  is_required?: boolean;
  is_generated?: boolean;
}

export interface DeliverableStage {
  stage: string;
  total: number;
  completed: number;
  progress: number;
}

export interface DeliverableCollectResponse {
  success: boolean;
  message: string;
  collected_count: number;
  deliverables: ProjectDeliverable[];
}

export interface DeliverableGenerateResponse {
  success: boolean;
  message: string;
  deliverable?: ProjectDeliverable;
}

export const projectDeliverablesApi = {
  getAll: async (params?: { stage?: string; category?: string; status?: string }): Promise<ProjectDeliverable[]> => {
    const { data } = await api.get('/project-deliverables', { params });
    return data;
  },
  getStages: async (): Promise<DeliverableStage[]> => {
    const { data } = await api.get('/project-deliverables/stages');
    return data;
  },
  getById: async (id: number): Promise<ProjectDeliverable> => {
    const { data } = await api.get(`/project-deliverables/${id}`);
    return data;
  },
  create: async (deliverable: ProjectDeliverableCreate): Promise<ProjectDeliverable> => {
    const { data } = await api.post('/project-deliverables', deliverable);
    return data;
  },
  update: async (id: number, deliverable: ProjectDeliverableUpdate): Promise<ProjectDeliverable> => {
    const { data } = await api.put(`/project-deliverables/${id}`, deliverable);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/project-deliverables/${id}`);
  },
  collect: async (params?: { stage?: string; include_empty?: boolean }): Promise<DeliverableCollectResponse> => {
    const { data } = await api.post('/project-deliverables/collect', params || {});
    return data;
  },
  generateFile: async (id: number): Promise<{ success: boolean; message: string; file_name: string; file_size: number }> => {
    const { data } = await api.post(`/project-deliverables/${id}/generate-file`);
    return data;
  },
  generate: async (id: number, options?: { include_cover?: boolean; include_overview?: boolean; include_toc?: boolean }): Promise<DeliverableGenerateResponse> => {
    const { data } = await api.post(`/project-deliverables/${id}/generate`, {
      deliverable_id: id,
      include_cover: options?.include_cover ?? true,
      include_overview: options?.include_overview ?? true,
      include_toc: options?.include_toc ?? true,
    });
    return data;
  },
  download: async (id: number): Promise<{ redirect_url?: string } | Blob> => {
    const { data } = await api.get(`/project-deliverables/${id}/download`, {
      responseType: 'blob',
    });
    return data;
  },
  upload: async (id: number, file: File): Promise<{ success: boolean; message: string; deliverable?: Partial<ProjectDeliverable> }> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post(`/project-deliverables/${id}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },
};

// ==================== 인력 관리 ====================

export interface ProjectMember {
  id: number;
  member_id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
  department: string;
  grade: string;
  role_description?: string;
  google_id?: string;
  google_picture?: string;
  is_google_linked: boolean;
  status: string;
  join_date?: string;
  leave_date?: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectMemberCreate {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
  department: string;
  grade: string;
  role_description?: string;
  status?: string;
  join_date?: string;
  leave_date?: string;
  project_id?: number;
}

export interface ProjectMemberUpdate {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
  department?: string;
  grade?: string;
  role_description?: string;
  status?: string;
  join_date?: string;
  leave_date?: string;
  order_index?: number;
}

export interface MemberAllocation {
  id: number;
  project_id: number;
  member_id: number;
  start_date: string;
  end_date?: string;
  monthly_mm?: Record<string, number>;  // {"2025-11": 0.5, "2025-12": 1.0}
  project_role?: string;
  responsibilities?: string;
  status: string;
  notes?: string;
  // 멤버 정보 (조회 시 포함)
  member_name?: string;
  member_department?: string;
  member_grade?: string;
  member_company?: string;
  member_email?: string;
  google_picture?: string;
  member_member_id?: string;
  created_at: string;
  updated_at: string;
}

export interface MemberAllocationCreate {
  project_id: number;
  member_id: number;
  start_date: string;
  end_date?: string;
  monthly_mm?: Record<string, number>;
  project_role?: string;
  responsibilities?: string;
  status?: string;
  notes?: string;
}

export interface MemberAllocationUpdate {
  start_date?: string;
  end_date?: string;
  monthly_mm?: Record<string, number>;
  project_role?: string;
  responsibilities?: string;
  status?: string;
  notes?: string;
}

export interface AllocationSummary {
  project_id: number;
  total_members: number;
  total_mm: number;
  by_department: Record<string, { count: number; total_mm: number }>;
  by_month: Record<string, number>;
}

export const membersApi = {
  getAll: async (params?: { department?: string; grade?: string; status?: string }): Promise<ProjectMember[]> => {
    const { data } = await api.get('/members', { params });
    return data;
  },
  getById: async (id: number): Promise<ProjectMember> => {
    const { data } = await api.get(`/members/${id}`);
    return data;
  },
  create: async (member: ProjectMemberCreate): Promise<ProjectMember> => {
    const { data } = await api.post('/members', member);
    return data;
  },
  update: async (id: number, member: ProjectMemberUpdate): Promise<ProjectMember> => {
    const { data } = await api.put(`/members/${id}`, member);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/members/${id}`);
  },
  getDepartments: async (): Promise<{ code: string; name: string; color: string }[]> => {
    const { data } = await api.get('/members/options/departments');
    return data;
  },
  getGrades: async (): Promise<{ code: string; name: string; color: string }[]> => {
    const { data } = await api.get('/members/options/grades');
    return data;
  },
  getPositions: async (): Promise<{ code: string; name: string; color: string }[]> => {
    const { data } = await api.get('/members/options/positions');
    return data;
  },
  getStatuses: async (): Promise<{ code: string; name: string; color: string }[]> => {
    const { data } = await api.get('/members/options/statuses');
    return data;
  },
};

export const allocationsApi = {
  getAll: async (params?: { project_id?: number; member_id?: number; status?: string }): Promise<MemberAllocation[]> => {
    const { data } = await api.get('/allocations', { params });
    return data;
  },
  getById: async (id: number): Promise<MemberAllocation> => {
    const { data } = await api.get(`/allocations/${id}`);
    return data;
  },
  create: async (allocation: MemberAllocationCreate): Promise<MemberAllocation> => {
    const { data } = await api.post('/allocations', allocation);
    return data;
  },
  update: async (id: number, allocation: MemberAllocationUpdate): Promise<MemberAllocation> => {
    const { data } = await api.put(`/allocations/${id}`, allocation);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/allocations/${id}`);
  },
  getSummary: async (projectId: number): Promise<AllocationSummary> => {
    const { data } = await api.get(`/allocations/summary/${projectId}`);
    return data;
  },
};

// ==================== 초대 관리 ====================

export interface Invitation {
  id: number;
  project_id: number;
  email: string;
  role: string;
  department?: string;
  grade?: string;
  token: string;
  status: string;  // 대기, 수락, 만료, 취소
  invited_by: number;
  message?: string;
  expires_at: string;
  accepted_at?: string;
  created_at: string;
  // 관계 정보
  project_name?: string;
  project_code?: string;
  invited_by_name?: string;
}

export interface InvitationCreate {
  project_id: number;
  email: string;
  role?: string;
  department?: string;
  grade?: string;
  message?: string;
  expires_days?: number;
}

export interface InvitationVerifyResponse {
  valid: boolean;
  invitation?: Invitation;
  message?: string;
}

export interface GoogleAuthUrlResponse {
  auth_url: string;
}

export interface GoogleAuthCallbackResponse {
  success: boolean;
  message: string;
  user?: {
    id: number;
    username: string;
    email: string;
    name: string;
    company: string;
    position: string;
    role: 'admin' | 'viewer' | 'super_admin';
  };
  token?: string;
  invitation_id?: number;
}

export const invitationsApi = {
  getAll: async (params?: { project_id?: number; status?: string }): Promise<Invitation[]> => {
    const { data } = await api.get('/invitations', { params });
    return data;
  },
  create: async (invitation: InvitationCreate): Promise<Invitation> => {
    const { data } = await api.post('/invitations', invitation);
    return data;
  },
  verify: async (token: string): Promise<InvitationVerifyResponse> => {
    const { data } = await api.get(`/invitations/verify/${token}`);
    return data;
  },
  resend: async (id: number): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post(`/invitations/${id}/resend`);
    return data;
  },
  cancel: async (id: number): Promise<void> => {
    await api.delete(`/invitations/${id}`);
  },
};

export const googleAuthApi = {
  getAuthUrl: async (invitationToken?: string): Promise<GoogleAuthUrlResponse> => {
    const redirectUri = typeof window !== 'undefined'
      ? `${window.location.origin}/settings/system/oauth-callback`
      : 'http://localhost:4211/settings/system/oauth-callback';
    const params: Record<string, string> = { redirect_uri: redirectUri };
    if (invitationToken) {
      params.invitation_token = invitationToken;
    }
    const { data } = await api.get('/auth/google/url', { params });
    return data;
  },
  callback: async (code: string, redirectUri: string, invitationToken?: string): Promise<GoogleAuthCallbackResponse> => {
    const { data } = await api.post('/auth/google/callback', {
      code,
      redirect_uri: redirectUri,
      invitation_token: invitationToken,
    });
    return data;
  },
};

// ==================== AI 채팅 ====================

export interface ChatMessage {
  id: number;
  session_id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  token_count?: number;
  created_at: string;
}

export interface ChatSession {
  id: number;
  project_id?: number;
  user_id?: number;
  title?: string;
  chat_mode: 'qa' | 'tool';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface ChatSessionDetail extends ChatSession {
  messages: ChatMessage[];
}

export interface ChatMode {
  mode: string;
  label: string;
  description: string;
  available: boolean;
}

export const chatApi = {
  getSessions: async (limit = 20): Promise<ChatSession[]> => {
    const { data } = await api.get('/chat/sessions', { params: { limit } });
    return data;
  },
  getSession: async (sessionId: number): Promise<ChatSessionDetail> => {
    const { data } = await api.get(`/chat/sessions/${sessionId}`);
    return data;
  },
  deleteSession: async (sessionId: number): Promise<void> => {
    await api.delete(`/chat/sessions/${sessionId}`);
  },
  sendMessage: async (content: string, sessionId?: number, mode?: string): Promise<{ session_id: number; message: ChatMessage; tool_results?: unknown[] }> => {
    const { data } = await api.post('/chat/send', { content, session_id: sessionId, mode: mode || 'qa' }, { params: { stream: false } });
    return data;
  },
  sendMessageStream: (content: string, sessionId?: number): EventSource | null => {
    // SSE는 POST 미지원이라 fetch 사용 필요 → chatStreamFetch 사용
    return null;
  },
  getModes: async (): Promise<ChatMode[]> => {
    const { data } = await api.get('/chat/modes');
    return data;
  },
  getSdkAuthStatus: async (): Promise<{ authenticated: boolean; cli_installed: boolean; message: string }> => {
    const { data } = await api.get('/chat/sdk/auth-status');
    return data;
  },
  sdkLogin: async (): Promise<{ success: boolean; login_url?: string; message?: string }> => {
    const { data } = await api.post('/chat/sdk/login');
    return data;
  },
};

// ==================== 산출물 출력 ====================

export interface ExportStats {
  requirements: number;
  screen_designs: number;
  table_definitions: number;
  api_specs: number;
  test_scenarios: number;
  test_cases: number;
  test_runs: number;
}

export const exportApi = {
  getStats: async (): Promise<ExportStats> => {
    const { data } = await api.get('/export/stats');
    return data;
  },
  downloadRequirements: () => `${API_BASE}/export/requirements`,
  downloadTableDefinitions: () => `${API_BASE}/export/table-definitions`,
  downloadApiSpecs: () => `${API_BASE}/export/api-specs`,
  downloadTestResults: () => `${API_BASE}/export/test-results`,
  downloadTraceability: () => `${API_BASE}/export/traceability`,
  downloadAuditPackage: () => `${API_BASE}/export/audit-package`,
};

// ==================== 회의록 ====================

export interface MeetingAgendaItem { content: string; order_index?: number }
export interface MeetingDecisionItem { id?: number; content: string; related_entity_type?: string; related_entity_id?: string; order_index?: number }
export interface MeetingActionItem { id?: number; content: string; assignee?: string; due_date?: string; status?: string; task_id?: number; order_index?: number }

export interface Meeting {
  id: number;
  project_id?: number;
  meeting_id: string;
  title: string;
  meeting_date: string;
  location?: string;
  attendees?: string[];
  recorder?: string;
  status: string;
  requested_by?: number;
  approved_by?: number;
  approved_at?: string;
  reject_reason?: string;
  ai_summary?: string;
  created_by?: number;
  creator_name?: string;
  approver_name?: string;
  agendas?: MeetingAgendaItem[];
  decisions?: MeetingDecisionItem[];
  actions?: MeetingActionItem[];
  created_at: string;
  updated_at: string;
}

export interface MeetingCreate {
  title: string;
  meeting_date: string;
  location?: string;
  attendees?: string[];
  recorder?: string;
  agendas?: MeetingAgendaItem[];
  decisions?: MeetingDecisionItem[];
  actions?: MeetingActionItem[];
}

export interface MeetingUpdate {
  title?: string;
  meeting_date?: string;
  location?: string;
  attendees?: string[];
  recorder?: string;
  ai_summary?: string;
  agendas?: MeetingAgendaItem[];
  decisions?: MeetingDecisionItem[];
  actions?: MeetingActionItem[];
}

export const meetingsApi = {
  getAll: async (params?: { status?: string }): Promise<Meeting[]> => {
    const { data } = await api.get('/meetings', { params });
    return data;
  },
  getById: async (id: number): Promise<Meeting> => {
    const { data } = await api.get(`/meetings/${id}`);
    return data;
  },
  create: async (meeting: MeetingCreate): Promise<Meeting> => {
    const { data } = await api.post('/meetings', meeting);
    return data;
  },
  update: async (id: number, meeting: MeetingUpdate): Promise<Meeting> => {
    const { data } = await api.put(`/meetings/${id}`, meeting);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/meetings/${id}`);
  },
  requestApproval: async (id: number): Promise<Meeting> => {
    const { data } = await api.post(`/meetings/${id}/request-approval`);
    return data;
  },
  approve: async (id: number): Promise<Meeting> => {
    const { data } = await api.post(`/meetings/${id}/approve`);
    return data;
  },
  reject: async (id: number, reason: string): Promise<Meeting> => {
    const { data } = await api.post(`/meetings/${id}/reject`, { reason });
    return data;
  },
  createTaskFromAction: async (meetingId: number, actionId: number): Promise<{ success: boolean; task_id: number; task_name: string }> => {
    const { data } = await api.post(`/meetings/${meetingId}/actions/${actionId}/create-task`);
    return data;
  },
  generateAiSummary: async (id: number): Promise<{ summary: string }> => {
    const { data } = await api.post(`/meetings/${id}/ai-summary`);
    return data;
  },
  linkRequirement: async (meetingId: number, decisionId: number, requirementId: number): Promise<{ success: boolean; req_id: string; req_name: string }> => {
    const { data } = await api.put(`/meetings/${meetingId}/decisions/${decisionId}/link-requirement`, { requirement_id: requirementId });
    return data;
  },
  createRequirementFromDecision: async (meetingId: number, decisionId: number): Promise<{ success: boolean; req_id: string; req_name: string }> => {
    const { data } = await api.post(`/meetings/${meetingId}/decisions/${decisionId}/create-requirement`);
    return data;
  },
};

// ==================== 알림 ====================

export interface Notification {
  id: number;
  user_id: number;
  type: string;  // mention, comment, status_change, deadline
  title: string;
  content?: string;
  link?: string;
  entity_type?: string;
  entity_id?: string;
  is_read: boolean;
  actor_id?: number;
  actor_name?: string;
  project_id?: number;
  created_at: string;
}

export const notificationsApi = {
  getAll: async (params?: { unread_only?: boolean; limit?: number; offset?: number }): Promise<{ items: Notification[]; total: number }> => {
    const { data } = await api.get('/notifications', { params });
    return data;
  },
  getUnreadCount: async (): Promise<number> => {
    const { data } = await api.get('/notifications/unread-count');
    return data.count;
  },
  markAsRead: async (id: number): Promise<void> => {
    await api.put(`/notifications/${id}/read`);
  },
  markAllAsRead: async (): Promise<void> => {
    await api.put('/notifications/read-all');
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/notifications/${id}`);
  },
  deleteAll: async (): Promise<void> => {
    await api.delete('/notifications');
  },
};

// ==================== 범용 댓글/멘션 ====================

export interface CommentMention {
  user_id: number;
  username: string;
}

export interface Comment {
  id: number;
  project_id?: number;
  entity_type: string;
  entity_id: string;
  parent_id?: number;
  user_id: number;
  content: string;
  mentions?: CommentMention[];
  username?: string;
  display_name?: string;
  reply_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CommentCreate {
  content: string;
  parent_id?: number;
  mentions?: CommentMention[];
}

export const commentsApi = {
  getComments: async (entityType: string, entityId: string): Promise<Comment[]> => {
    const { data } = await api.get(`/comments/${entityType}/${entityId}`);
    return data;
  },
  getReplies: async (entityType: string, entityId: string, commentId: number): Promise<Comment[]> => {
    const { data } = await api.get(`/comments/${entityType}/${entityId}/${commentId}/replies`);
    return data;
  },
  create: async (entityType: string, entityId: string, comment: CommentCreate): Promise<Comment> => {
    const { data } = await api.post(`/comments/${entityType}/${entityId}`, comment);
    return data;
  },
  update: async (commentId: number, content: string, mentions?: CommentMention[]): Promise<Comment> => {
    const { data } = await api.put(`/comments/${commentId}`, { content, mentions });
    return data;
  },
  delete: async (commentId: number): Promise<void> => {
    await api.delete(`/comments/${commentId}`);
  },
  getCount: async (entityType: string, entityId: string): Promise<number> => {
    const { data } = await api.get(`/comments/count/${entityType}/${entityId}`);
    return data.count;
  },
};

// SSE 스트리밍용 fetch 함수
export async function chatStreamFetch(
  content: string,
  sessionId: number | null,
  onChunk: (text: string) => void,
  onSessionId: (id: number) => void,
  onDone: () => void,
  onError: (err: string) => void,
): Promise<void> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('wbs_auth_token') : null;

  const response = await fetch(`${API_BASE}/chat/send?stream=true`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ content, session_id: sessionId }),
  });

  if (!response.ok) {
    const err = await response.text();
    onError(err);
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onError('스트리밍을 시작할 수 없습니다.');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'session') {
            onSessionId(data.session_id);
          } else if (data.type === 'content') {
            onChunk(data.text);
          } else if (data.type === 'done') {
            onDone();
          } else if (data.type === 'error') {
            onError(data.text);
          }
        } catch {
          // JSON 파싱 실패 무시
        }
      }
    }
  }
}

export default api;
